import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import { CreateSalespersonData } from "../../types/api";
import toast from "react-hot-toast";

interface AddSalespersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddSalespersonModal: React.FC<AddSalespersonModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateSalespersonData>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    employmentDate: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Login creation state
  const [createLogin, setCreateLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginPasswordConfirm, setLoginPasswordConfirm] = useState("");
  const [loginUserType, setLoginUserType] = useState<"salesperson" | "manager">("salesperson");
  const [monthlyMinuteLimit, setMonthlyMinuteLimit] = useState<string>("");
  const [hasLimit, setHasLimit] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return "Password must contain at least one special character (@$!%*?&)";
    }
    return null;
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = "First name must be less than 100 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = "Last name must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "Description must be less than 1000 characters";
    }

    // Date validations
    if (formData.dateOfBirth && new Date(formData.dateOfBirth) > new Date()) {
      newErrors.dateOfBirth = "Date of birth cannot be in the future";
    }

    if (
      formData.employmentDate &&
      new Date(formData.employmentDate) > new Date()
    ) {
      newErrors.employmentDate = "Employment date cannot be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Validate login fields if creating login
    if (createLogin) {
      const newErrors: { [key: string]: string } = {};

      if (!loginEmail.trim()) {
        newErrors.loginEmail = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
        newErrors.loginEmail = "Please enter a valid email";
      }

      if (!loginPassword) {
        newErrors.loginPassword = "Password is required";
      } else {
        const passwordError = validatePassword(loginPassword);
        if (passwordError) {
          newErrors.loginPassword = passwordError;
        }
      }

      if (!loginPasswordConfirm) {
        newErrors.loginPasswordConfirm = "Please confirm your password";
      } else if (loginPassword !== loginPasswordConfirm) {
        newErrors.loginPasswordConfirm = "Passwords do not match";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...newErrors }));
        return;
      }
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        dateOfBirth: formData.dateOfBirth || undefined,
        employmentDate: formData.employmentDate || undefined,
        description: formData.description || undefined,
        monthlyMinuteLimit:
          hasLimit && monthlyMinuteLimit
            ? parseInt(monthlyMinuteLimit) * 60
            : null,
      };

      const response = await apiService.createSalesperson(submitData);

      if (response.success && response.data) {
        // If creating login, create it now
        if (createLogin) {
          const loginResponse = await apiService.createSalespersonLogin(
            response.data.id,
            { email: loginEmail, password: loginPassword, userType: loginUserType }
          );

          if (!loginResponse.success) {
            toast.error("Salesperson created but failed to create login");
          } else {
            toast.success("Salesperson and login created successfully!");
          }
        } else {
          toast.success("Salesperson added successfully!");
        }

        handleClose();
        onSuccess();
      } else {
        toast.error(response.message || "Failed to add salesperson");
      }
    } catch (error: any) {
      console.error("Add salesperson error:", error);
      const message =
        error.response?.data?.message || "Failed to add salesperson";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        employmentDate: "",
        description: "",
      });
      setErrors({});
      setCreateLogin(false);
      setLoginEmail("");
      setLoginPassword("");
      setLoginPasswordConfirm("");
      setLoginUserType("salesperson");
      setMonthlyMinuteLimit("");
      setHasLimit(false);
      onClose();
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Add New Salesperson
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Add a new team member to track their sales call
                      performance.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* First Name and Last Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.firstName
                            ? "border-red-300"
                            : "border-gray-300"
                        } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                        placeholder="First name"
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.lastName ? "border-red-300" : "border-gray-300"
                        } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                        placeholder="Last name"
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label
                      htmlFor="dateOfBirth"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      id="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.dateOfBirth
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                    />
                    {errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div>

                  {/* Employment Date */}
                  <div>
                    <label
                      htmlFor="employmentDate"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Employment Date
                    </label>
                    <input
                      type="date"
                      name="employmentDate"
                      id="employmentDate"
                      value={formData.employmentDate}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.employmentDate
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                    />
                    {errors.employmentDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.employmentDate}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.description
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                      placeholder="Additional notes about the salesperson..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  {/* Monthly Limit */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Set Monthly Upload Limit
                        </h4>
                        <p className="text-xs text-gray-500">
                          Restrict how many minutes this salesperson can upload
                          per month
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setHasLimit(!hasLimit);
                          if (!hasLimit) {
                            setMonthlyMinuteLimit("");
                          }
                        }}
                        className={`${
                          hasLimit ? "bg-indigo-600" : "bg-gray-200"
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                      >
                        <span
                          className={`${
                            hasLimit ? "translate-x-5" : "translate-x-0"
                          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </div>

                    {hasLimit && (
                      <div className="pl-4 border-l-2 border-indigo-200">
                        <label
                          htmlFor="monthlyMinuteLimit"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Monthly Limit (minutes)
                        </label>
                        <input
                          type="number"
                          name="monthlyMinuteLimit"
                          id="monthlyMinuteLimit"
                          min="1"
                          value={monthlyMinuteLimit}
                          onChange={(e) =>
                            setMonthlyMinuteLimit(e.target.value)
                          }
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                          placeholder="e.g., 1000"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Leave blank for unlimited. This salesperson can upload
                          up to this many minutes per month.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Create Login Toggle */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Create Login Account
                        </h4>
                        <p className="text-xs text-gray-500">
                          Allow this salesperson to access the system
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCreateLogin(!createLogin)}
                        className={`${
                          createLogin ? "bg-indigo-600" : "bg-gray-200"
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                      >
                        <span
                          className={`${
                            createLogin ? "translate-x-5" : "translate-x-0"
                          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </div>

                    {createLogin && (
                      <div className="space-y-4 pl-4 border-l-2 border-indigo-200">
                        <div>
                          <label
                            htmlFor="loginEmail"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Login Email *
                          </label>
                          <input
                            type="email"
                            name="loginEmail"
                            id="loginEmail"
                            value={loginEmail}
                            onChange={(e) => {
                              setLoginEmail(e.target.value);
                              if (errors.loginEmail) {
                                setErrors((prev) => ({
                                  ...prev,
                                  loginEmail: "",
                                }));
                              }
                            }}
                            className={`mt-1 block w-full rounded-md border ${
                              errors.loginEmail
                                ? "border-red-300"
                                : "border-gray-300"
                            } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                            placeholder="email@example.com"
                          />
                          {errors.loginEmail && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.loginEmail}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="loginPassword"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Password *
                          </label>
                          <input
                            type="password"
                            name="loginPassword"
                            id="loginPassword"
                            value={loginPassword}
                            onChange={(e) => {
                              setLoginPassword(e.target.value);
                              if (errors.loginPassword) {
                                setErrors((prev) => ({
                                  ...prev,
                                  loginPassword: "",
                                }));
                              }
                            }}
                            onBlur={() => {
                              // Validate on blur
                              if (loginPassword) {
                                const passwordError =
                                  validatePassword(loginPassword);
                                if (passwordError) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    loginPassword: passwordError,
                                  }));
                                }
                              }
                            }}
                            className={`mt-1 block w-full rounded-md border ${
                              errors.loginPassword
                                ? "border-red-300"
                                : "border-gray-300"
                            } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                            placeholder="••••••••"
                          />
                          {errors.loginPassword && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.loginPassword}
                            </p>
                          )}
                          {!errors.loginPassword && (
                            <p className="mt-1 text-xs text-gray-500">
                              Must be at least 8 characters with uppercase,
                              lowercase, number, and special character
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="loginPasswordConfirm"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Confirm Password *
                          </label>
                          <input
                            type="password"
                            name="loginPasswordConfirm"
                            id="loginPasswordConfirm"
                            value={loginPasswordConfirm}
                            onChange={(e) => {
                              setLoginPasswordConfirm(e.target.value);
                              if (errors.loginPasswordConfirm) {
                                setErrors((prev) => ({
                                  ...prev,
                                  loginPasswordConfirm: "",
                                }));
                              }
                            }}
                            onBlur={() => {
                              // Validate on blur
                              if (
                                loginPasswordConfirm &&
                                loginPassword !== loginPasswordConfirm
                              ) {
                                setErrors((prev) => ({
                                  ...prev,
                                  loginPasswordConfirm:
                                    "Passwords do not match",
                                }));
                              }
                            }}
                            className={`mt-1 block w-full rounded-md border ${
                              errors.loginPasswordConfirm
                                ? "border-red-300"
                                : "border-gray-300"
                            } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                            placeholder="••••••••"
                          />
                          {errors.loginPasswordConfirm && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.loginPasswordConfirm}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Access Level
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setLoginUserType("salesperson")}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                loginUserType === "salesperson"
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "salesperson" ? "bg-indigo-500" : "bg-gray-300"}`} />
                              Salesperson
                            </button>
                            <button
                              type="button"
                              onClick={() => setLoginUserType("manager")}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                loginUserType === "manager"
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "manager" ? "bg-indigo-500" : "bg-gray-300"}`} />
                              Admin
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {loginUserType === "manager"
                              ? "Admin can manage employees, view all calls, and configure settings."
                              : "Salesperson can only view their own calls and upload recordings."}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Adding...
                        </>
                      ) : (
                        "Add Salesperson"
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddSalespersonModal;
